import result, { type PossibleTypesResultData } from '../generated/apollo';
import type { InterfaceTypes, Maybe } from '../generated/types';
import type { PageInfo, ObjectTypes } from '../generated/types';

// --------------------------------------------------------------------------------------- //
//  Abstract Output Types                                                                  //
// --------------------------------------------------------------------------------------- //

/**
 * Reference to the `PossibleTypes` map.
 */
export type PossibleTypes = PossibleTypesResultData['possibleTypes'];

/**
 * Type that matches all the `Union` and `Interface` typenames.
 */
export type AbstractTypename = keyof PossibleTypes;

/**
 * Matches all possible object types of that implement `Union` or `Interface` type.
 */
export type PossibleTypename<K extends AbstractTypename> =
    PossibleTypes[K][number];

/**
 * Matches any GraphQL-object that has a `__typename` which is a possible type for the
 * abstract type with name `K`.
 */
export type AnyPossibleType<K extends AbstractTypename> = {
    __typename?: PossibleTypename<K>;
};

/**
 * The possible types map.
 */
export const possibleTypes = result.possibleTypes;

// --------------------------------------------------------------------------------------- //
//  Interface Types                                                                        //
// --------------------------------------------------------------------------------------- //

/**
 * Matches the typenames of the interface types.
 */
export type InterfaceTypename = keyof InterfaceTypes;

/**
 * Gives the interface type with the provided typename.
 */
export type InterfaceType<K extends InterfaceTypename> = InterfaceTypes[K];

// --------------------------------------------------------------------------------------- //
//  Object Types                                                                           //
// --------------------------------------------------------------------------------------- //

/**
 * Matches the typenames of the object types.
 */
export type ObjectTypename = keyof ObjectTypes;

/**
 * Matches the object type with the provided typename.
 */
export type ObjectType<K extends ObjectTypename = ObjectTypename> =
    ObjectTypes[K];

/**
 * Matches all object that have an object-typename.
 */
export type AnyObjectType = { __typename?: ObjectTypename };

// --------------------------------------------------------------------------------------- //
//  Connections                                                                            //
// --------------------------------------------------------------------------------------- //

/**
 * Matches the typenames of the connection types.
 */
export type ConnectionTypename = `${string}Connection` & ObjectTypename;

/**
 * Matches any connection type.
 */
export type ConnectionType = ObjectTypes[ConnectionTypename];

/**
 * Matches any object that have a ConnectionTypename as its `__typename`.
 */
export type AnyConnectionType = { __typename?: ConnectionTypename };

/**
 * Matches the typenames of the edge types.
 */
export type EdgeTypename = `${string}Edge` & ObjectTypename;

/**
 * Matches any connection type.
 */
export type EdgeType = ObjectTypes[EdgeTypename];

/**
 * The edge typename of a Connection with typename `K`.
 */
export type EdgeTypenameForConnection<K extends ConnectionTypename> =
    ObjectTypes[K] extends {
        edges: Maybe<Array<Maybe<{ __typename: infer Typename }>>>;
    }
        ? Typename & EdgeTypename
        : never;

/**
 * The type of the edges in the provided connection.
 */
export type EdgeForConnection<C extends AnyConnectionType> = C extends {
    edges?: Maybe<Array<Maybe<infer E>>>;
}
    ? E
    : C['__typename'] extends ConnectionTypename
    ? { __typename?: EdgeTypenameForConnection<C['__typename']> }
    : never;

/**
 * Matches any object that have a EdgeTypename as its `__typename`.
 */
export type AnyEdgeType = { __typename?: EdgeTypename };

/**
 * Matches the node typenames.
 */
export type NodeTypename = PossibleTypename<'Node'>;

/**
 * Matches any object that has a NodeTypename as its `__typename`.
 */
export type AnyNodeType = AnyPossibleType<'Node'>;

/**
 * The node typename of an edge with typename `K`.
 */
export type NodeTypenameForEdge<K extends EdgeTypename> =
    ObjectTypes[K]['node'] extends Maybe<{ __typename?: infer N }> ? N : never;

export type NodeForEdge<E extends AnyEdgeType> = E extends {
    node?: Maybe<infer N>;
}
    ? N
    : E['__typename'] extends EdgeTypename
    ? { __typename?: NodeTypenameForEdge<E['__typename']> }
    : never;

/**
 * The node typename of a connection.
 */
export type NodeTypenameForConnection<K extends ConnectionTypename> =
    NodeTypenameForEdge<EdgeTypenameForConnection<K>>;

/**
 * The type of the nodes in the connection.
 */
export type NodeForConnection<C extends AnyConnectionType> = C extends {
    nodes?: Maybe<Array<Maybe<infer N>>>;
}
    ? N
    : EdgeForConnection<C> extends AnyEdgeType
    ? NodeForEdge<EdgeForConnection<C>>
    : never;

/**
 * The type of the PageInfo object of a connection.
 */
export type PageInfoForConnection<C extends AnyConnectionType> = C extends {
    pageInfo?: infer P;
}
    ? P & Partial<PageInfo>
    : Partial<PageInfo>;
